import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from '@wovendays/i18n';
import { apiClient } from '@/lib/api-client';
import { ApiClientError } from '@/lib/api-client';
import { Tile } from '@/components/ui/tile';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface InvitePreview {
  expired: boolean;
  familyName: string | null;
  memberCount: number;
  inviterName: string | null;
}

interface AcceptResult {
  family: {
    id: string;
    name: string;
    ownerId: string;
    createdAt: number;
  };
  member: {
    id: string;
    familyId: string;
    userId: string;
    role: string;
    joinedAt: number;
  };
}

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('invite');
  const [result, setResult] = useState<AcceptResult | null>(null);

  const previewQuery = useQuery({
    queryKey: ['invite-preview', token],
    queryFn: () => apiClient.get<InvitePreview>(`/api/invites/${token}/preview`),
    enabled: !!token,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.post<AcceptResult>(`/api/invites/${token}/accept`),
    onSuccess: (data) => {
      setResult(data);
    },
  });

  // Success state — user joined the family
  if (result) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4">
        <div className="w-full max-w-sm grid gap-2">
          <Tile variant="dark" className="p-8 text-center">
            <div className="mb-4 text-4xl" role="img" aria-label="Celebration">&#127881;</div>
            <h2 className="font-serif text-xl text-white mb-2">
              {t('success.title', { familyName: result.family.name })}
            </h2>
            <p className="font-mono text-xs text-zinc-400">
              {t('success.description', { role: result.member.role })}
            </p>
          </Tile>
          <button
            onClick={() => navigate(`/families/${result.family.id}/timeline`)}
            className="w-full rounded-2xl bg-primary-600 px-6 py-4 font-mono text-sm uppercase tracking-wide text-white transition-all hover:bg-primary-700 hover:-translate-y-px hover:shadow-elevated-hover active:translate-y-0"
          >
            {t('success.goToTimeline')}
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (previewQuery.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4" role="status" aria-label={t('loading')}>
        <div className="w-full max-w-sm grid gap-2">
          <Tile variant="dark" className="p-8 text-center">
            <div className="animate-pulse space-y-3">
              <div className="h-6 bg-zinc-700 rounded w-3/4 mx-auto" />
              <div className="h-4 bg-zinc-700 rounded w-1/2 mx-auto" />
            </div>
          </Tile>
        </div>
      </div>
    );
  }

  // Expired invite
  if (previewQuery.data?.expired) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4">
        <div className="w-full max-w-sm grid gap-2">
          <Tile variant="dark" className="p-8 text-center">
            <h2 className="font-serif text-xl text-white mb-2">{t('expired.title')}</h2>
            <p className="font-mono text-xs text-zinc-400">
              {t('expired.description')}
            </p>
          </Tile>
          <Link to="/" className="block">
            <Tile variant="accent" interactive className="p-4 text-center">
              <span className="font-mono text-sm uppercase tracking-wide text-white">{t('expired.goHome')}</span>
            </Tile>
          </Link>
        </div>
      </div>
    );
  }

  // Not found (404 from API)
  if (previewQuery.isError) {
    const is404 = previewQuery.error instanceof ApiClientError && previewQuery.error.status === 404;
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4">
        <div className="w-full max-w-sm grid gap-2">
          <Tile variant="dark" className="p-8 text-center">
            <h2 className="font-serif text-xl text-white mb-2">
              {is404 ? t('notFound.title') : t('error.title')}
            </h2>
            <p className="font-mono text-xs text-zinc-400">
              {is404
                ? t('notFound.description')
                : t('error.description')}
            </p>
          </Tile>
          <Link to="/" className="block">
            <Tile variant="accent" interactive className="p-4 text-center">
              <span className="font-mono text-sm uppercase tracking-wide text-white">{t('expired.goHome')}</span>
            </Tile>
          </Link>
        </div>
      </div>
    );
  }

  // Valid invite — show preview with family info
  const preview = previewQuery.data;

  return (
    <div className="flex min-h-[50vh] items-center justify-center px-4">
      <div className="w-full max-w-sm grid gap-2">
        <Tile variant="dark" className="p-8 text-center">
          <h2 className="font-serif text-xl text-white mb-2">
            {t('accept.title', { familyName: preview?.familyName ?? 'Family' })}
          </h2>
          <p className="font-mono text-xs text-zinc-400 mb-1">
            {t('accept.description', { familyName: preview?.familyName })}
          </p>
          {preview?.memberCount !== undefined && preview.memberCount > 0 && (
            <p className="font-mono text-xs text-zinc-500">
              {t('accept.memberCount', { count: preview.memberCount })}
            </p>
          )}
          {preview?.inviterName && (
            <p className="font-mono text-xs text-zinc-500 mt-1">
              {t('accept.invitedBy', { name: preview.inviterName })}
            </p>
          )}
        </Tile>

        {mutation.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {mutation.error instanceof Error ? mutation.error.message : t('accept.failed')}
            </AlertDescription>
          </Alert>
        )}

        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="w-full rounded-2xl bg-primary-600 px-6 py-4 font-mono text-sm uppercase tracking-wide text-white transition-all hover:bg-primary-700 hover:-translate-y-px hover:shadow-elevated-hover active:translate-y-0 disabled:opacity-50 disabled:pointer-events-none"
        >
          {mutation.isPending ? t('accept.joining') : t('accept.acceptInvite')}
        </button>
      </div>
    </div>
  );
}
